import React, { useMemo, useCallback } from 'react';

import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

import GlobalShortcuts, {
	type GlobalShortcutsItem,
	type GlobalShortcutsProps,
	productLinksWorkspaceCloudIdFilter,
} from '@atlassian/global-shortcuts';
import { companyHub } from '@atlassian/global-shortcuts/company-hub';
import { jira } from '@atlassian/global-shortcuts/jira';
import { loom } from '@atlassian/global-shortcuts/loom';
import { teams } from '@atlassian/global-shortcuts/teams';

import { fg } from '@confluence/feature-gating';
import { COMPANY_HUB } from '@confluence/named-routes';
import { useSessionData } from '@confluence/session-data';

import { GlobalItemLoadables } from './GlobalItems/GlobalItemLoadables';
import { useOrderedAppShortcuts } from './useOrderedAppShortcuts';
import { VisuallyHiddenDivider } from './GlobalItems/VisuallyHiddenDivider';

function companyHubComponent(): GlobalShortcutsItem {
	return {
		...companyHub(() => COMPANY_HUB.toUrl()),
		component: GlobalItemLoadables.companyHub,
	};
}

type GlobalAppShortcutsProps = {
	orderedMenuConfig?: { menuId: string; visible: boolean }[];
	onUpdateGetFilteredProductLinks?: GlobalShortcutsProps['onUpdateGetFilteredProductLinks'];
};

export const GlobalAppShortcuts = ({
	orderedMenuConfig = [],
	onUpdateGetFilteredProductLinks,
}: GlobalAppShortcutsProps) => {
	const { cloudId, orgId } = useSessionData();
	const { href } = navigateToTeamsApp({
		type: 'LANDING',
		cloudId,
		orgId,
		userHasNav4Enabled: true,
	});

	const updateGetFilteredProductLinks = useCallback(
		(getFilteredProductLinks) => {
			if (onUpdateGetFilteredProductLinks) {
				onUpdateGetFilteredProductLinks(getFilteredProductLinks);
			}
		},
		[onUpdateGetFilteredProductLinks],
	);

	// Map shortcut configs to menu identifiers
	const shortcutItemsConfig = useMemo(() => {
		return {
			companyHubShortcut: companyHubComponent(),
			jiraShortcut: jira(() => ''),
			loomShortcut: loom(() => ''),
			...(fg('nav4_gas_include_teams') ? { teamsShortcut: teams(() => href) } : {}),
		};
	}, [href]);

	// Sort shortcut items based on the ordered menu config
	const orderedShortcuts = useOrderedAppShortcuts(shortcutItemsConfig, orderedMenuConfig);

	const productLinksFilter = cloudId ? productLinksWorkspaceCloudIdFilter(cloudId) : undefined;

	return (
		<>
			<GlobalShortcuts
				testId="global-app-shortcuts"
				cloudId={cloudId}
				orgId={orgId}
				shortcutItems={orderedShortcuts}
				promoteSite
				product="confluence"
				productLinksFilter={productLinksFilter}
				onUpdateGetFilteredProductLinks={updateGetFilteredProductLinks}
			/>
			{orderedShortcuts.length > 0 && <VisuallyHiddenDivider />}
		</>
	);
};
